// Pure helpers for editing the shared `general_mapping` rule array.
//
// Priority / status / inbound / custom-field cards all mutate the SAME
// `general_mapping` array, each keyed by the same `GeneralMatch`. To avoid
// one card clobbering another's field, callers MERGE into the rule that
// matches a scope (find-or-create), then let the wholesale PUT persist it.

import {
  PROVIDER_FAMILY,
  type GeneralMappingRule,
  type GeneralMatch,
  type InboundConfig,
  type ProviderConfig,
} from "@/lib/types/itsm-config";

// Normalize a match value: undefined / null / "" all collapse to null so
// "no scope" (the tenant-wide default) compares equal regardless of source.
function norm(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

/** The empty (all-wildcard) match — the tenant-wide default rule's key. */
export const EMPTY_MATCH: GeneralMatch = {
  integration_id: null,
  provider_scope: null,
  issue_type: null,
};

/** True if two matches target the same scope (null-insensitive). */
export function sameMatch(a: GeneralMatch, b: GeneralMatch): boolean {
  return (
    norm(a.integration_id) === norm(b.integration_id) &&
    norm(a.provider_scope) === norm(b.provider_scope) &&
    norm(a.issue_type) === norm(b.issue_type)
  );
}

/** True if a match has no predicates set (the tenant-wide default). */
export function isEmptyMatch(m: GeneralMatch): boolean {
  return sameMatch(m, EMPTY_MATCH);
}

/** Find the rule for a given match, or undefined. */
export function findRule(
  rules: GeneralMappingRule[] | null | undefined,
  match: GeneralMatch,
): GeneralMappingRule | undefined {
  return (rules ?? []).find((r) => sameMatch(r.match, match));
}

/** The tenant-wide default rule (empty match), or undefined. */
export function defaultRule(
  rules: GeneralMappingRule[] | null | undefined,
): GeneralMappingRule | undefined {
  return findRule(rules, EMPTY_MATCH);
}

/** All rules with a non-empty match (scoped exceptions), preserving order. */
export function listScopedRules(
  rules: GeneralMappingRule[] | null | undefined,
): GeneralMappingRule[] {
  return (rules ?? []).filter((r) => !isEmptyMatch(r.match));
}

/**
 * True if an inbound block carries any setting. Every "is inbound empty?"
 * check goes through this, so a field added to InboundConfig can't be pruned
 * by one check and kept by another.
 */
export function hasInboundConfig(
  inbound: InboundConfig | null | undefined,
): boolean {
  return Boolean(
    inbound &&
      (inbound.watch != null ||
        inbound.since_date ||
        inbound.title_regex != null ||
        inbound.linked_only != null ||
        hasJiraInboundSettings(inbound) ||
        inbound.change_sync != null),
  );
}

/** True if an inbound block holds any Jira linked-only setting. */
export function hasJiraInboundSettings(
  inbound: InboundConfig | null | undefined,
): boolean {
  return Boolean(
    inbound &&
      (inbound.fields?.length ||
        (inbound.status_mapping &&
          Object.keys(inbound.status_mapping).length > 0) ||
        inbound.reopen != null ||
        inbound.dry_run != null),
  );
}

/**
 * The key the backend matches a Jira status name by: lowercase, with
 * whitespace, "-" and "/" read as "_".
 */
export function normalizeJiraStatusName(name: string): string {
  return name.trim().toLowerCase().replace(/[\s\-/]+/g, "_");
}

/**
 * True if any rule turns on linked-only import. The backend reads it
 * integration-wide, so one rule in any scope switches the whole pass.
 */
export function isLinkedOnly(
  rules: GeneralMappingRule[] | null | undefined,
): boolean {
  return (rules ?? []).some((r) => r.inbound?.linked_only === true);
}

/** True if a rule carries no config (safe to prune from the array). */
function isRuleEmpty(rule: GeneralMappingRule): boolean {
  const noPriority = !rule.priority_mapping ||
    Object.keys(rule.priority_mapping).length === 0;
  const noStatus = !rule.status_mapping ||
    Object.keys(rule.status_mapping).length === 0;
  const noCustom = !rule.custom_field_values ||
    Object.keys(rule.custom_field_values).length === 0;
  const noInbound = !hasInboundConfig(rule.inbound);
  const noOutbound = rule.outbound_sync == null;
  return noPriority && noStatus && noCustom && noInbound && noOutbound;
}

/**
 * Merge `patch` into the rule matching `match` (creating it if absent), then
 * prune any rule that ends up empty. Returns a NEW array — never mutates.
 * Other fields on the matched rule are preserved.
 */
export function upsertRuleField(
  rules: GeneralMappingRule[] | null | undefined,
  match: GeneralMatch,
  patch: Partial<Omit<GeneralMappingRule, "match">>,
): GeneralMappingRule[] {
  const existing = rules ?? [];
  const exists = existing.some((r) => sameMatch(r.match, match));
  const next = existing.map((r) =>
    sameMatch(r.match, match) ? { ...r, ...patch } : r,
  );
  if (!exists) {
    next.push({ match: { ...match }, ...patch });
  }
  return next.filter((r) => !isRuleEmpty(r));
}

/**
 * Pre-save check mirroring the backend's Jira rule: while linked-only is on,
 * every rule with an inbound block must name the fields it imports. Returns
 * the message to show, or null when the draft is fine.
 */
export function jiraInboundSaveError(draft: ProviderConfig): string | null {
  if (PROVIDER_FAMILY[draft.provider] !== "jira") return null;
  const rules = draft.general_mapping ?? [];
  if (!isLinkedOnly(rules)) return null;
  // The backend checks every rule with an inbound block, even an empty one.
  const missing = rules
    .filter((r) => r.inbound != null && !r.inbound.fields?.length)
    .map((r) => scopeName(r.match));
  if (!missing.length) return null;
  return (
    "Importing tickets: with linked-only on, pick at least one field to " +
    `import for ${missing.join(", ")}.`
  );
}

// RE2-only syntax rewritten so the browser can parse a pattern the collector
// compiles with RE2: leading inline flags like `(?i)` and `(?P<name>…)` named
// groups. JS and RE2 still disagree at the edges (JS accepts lookarounds and
// backreferences, which RE2 rejects), which is why the browser check is a
// hint, not a Save gate.
const LEADING_FLAGS = /^\(\?[aiLmsux]+\)/;

function toJsRegexSource(pattern: string): string {
  let source = pattern;
  while (LEADING_FLAGS.test(source)) source = source.replace(LEADING_FLAGS, "");
  return source
    .replace(/\(\?P</g, "(?<");
}

/**
 * Why an inbound title pattern looks invalid, or null when it parses. Only a
 * hint: the backend compiles it with RE2 on Save and has the final say.
 */
export function titleRegexError(
  pattern: string | null | undefined,
): string | null {
  if (pattern == null) return null;
  if (!pattern)
    return "Enter a pattern, or clear the field to import every title.";
  try {
    // eslint-disable-next-line security/detect-non-literal-regexp -- compiled only to validate; never executed
    new RegExp(toJsRegexSource(pattern));
    return null;
  } catch (e) {
    // V8 prefixes "Invalid regular expression: /<rewritten source>/: ".
    const reason = (e as Error).message.replace(/^.*\/: /, "");
    return `This doesn't look like a valid regular expression (${reason}).`;
  }
}

function scopeName(match: GeneralMatch): string {
  if (isEmptyMatch(match)) return "the default scope";
  return [norm(match.provider_scope), norm(match.issue_type)]
    .filter(Boolean)
    .join(" / ") || "a scoped rule";
}
